import toastr from 'toastr'
import moment from 'moment'
import { funcModal } from 'components/modals'
import { user, qr as qrcode, twoFactorPolicy, webauthn } from 'services'

const components = {
  funcModal
}

function firstErrorMessage (errors) {
  if (!errors) return null;
  if (errors.message) return errors.message;
  if (errors.errors) {
    const first = Object.keys(errors.errors)[0];
    if (first && errors.errors[first] && errors.errors[first][0]) {
      return errors.errors[first][0];
    }
  }
  return null;
}

function responseMessage (error, fallback) {
  const body = error && error.response && error.response.data;
  return (body && body.message) || fallback;
}

function secretFromOtpauth (uri) {
  if (!uri) return null;
  const match = String(uri).match(/[?&]secret=([^&]+)/i);
  return match ? decodeURIComponent(match[1]) : null;
}

function normalizeSetup (res) {
  const body = (res && res.data) || {};
  const nested = body.data && typeof body.data === 'object' ? body.data : {};
  const data = Object.assign({}, body, nested);
  const otpauthUri = data.otpauth_uri || data.otpauth_url || data.otpauthUri || data.uri || null;
  const secret = data.secret || data.google2fa_secret || data.otp_secret || secretFromOtpauth(otpauthUri);
  const recoveryCodes = data.recovery_codes || data.recoveryCodes || [];
  return { otpauthUri, secret, recoveryCodes };
}

const computed = {
  currentUser () {
    return this.$store.state.user.data || user.getUser();
  },
  enabled () {
    return twoFactorPolicy.enabled(this.currentUser);
  },
  twoFactorAction () {
    return twoFactorPolicy.normalize(this.currentUser && this.currentUser.two_fa_action)
  },
  twoFactorActionLabel () {
    return twoFactorPolicy.label(this.twoFactorAction)
  }
}

const methods = {
  storeErrors () {
    const errors = this.$store.state.user.errors;
    if (errors && errors.errors) {
      this.errors = _.cloneDeep(errors.errors);
    } else {
      this.errors = {};
    }
    return errors;
  },

  startSetup () {
    this.errors = {};
    this.processing = true;
    this.$store.dispatch('user.setup2fa').then((res) => {
      this.processing = false;
      const errors = this.storeErrors();
      if (errors) {
        toastr.error(firstErrorMessage(errors) || 'Unable to start 2FA setup');
        return;
      }
      const setup = normalizeSetup(res);
      this.secret = setup.secret;
      this.recoveryCodes = setup.recoveryCodes;
      this.setupActive = true;
      this.setupCode = '';
      this.qr = null;

      if (!setup.otpauthUri) {
        toastr.error('Setup did not return an otpauth URI');
        return;
      }
      try {
        this.qr = qrcode.toDataUrl(setup.otpauthUri, { width: 220, margin: 2 });
      } catch (e) {
        toastr.error('Unable to generate the 2FA QR code');
      }
    });
  },

  confirmSetup () {
    this.errors = {};
    this.processing = true;
    this.$store.dispatch('user.enable2fa', { code: this.setupCode }).then((res) => {
      this.processing = false;
      const errors = this.storeErrors();
      if (errors) {
        toastr.error(firstErrorMessage(errors) || 'Invalid verification code');
        return;
      }
      const setup = normalizeSetup(res);
      if (setup.recoveryCodes.length) {
        this.recoveryCodes = setup.recoveryCodes;
      }
      this.setupActive = false;
      this.setupCode = '';
      this.qr = null;
      this.secret = null;
      toastr.success('Two-factor authentication has been enabled');
    });
  },

  cancelSetup () {
    this.setupActive = false;
    this.setupCode = '';
    this.qr = null;
    this.secret = null;
    this.errors = {};
  },

  openDisable () {
    this.errors = {};
    this.disableCode = '';
    this.$refs.disableModal.open();
  },

  disable (modal) {
    this.errors = {};
    this.processing = true;
    this.$store.dispatch('user.disable2fa', { code: this.disableCode }).then(() => {
      this.processing = false;
      const errors = this.storeErrors();
      if (errors) {
        toastr.error(firstErrorMessage(errors) || 'Unable to disable 2FA');
        return;
      }
      this.disableCode = '';
      this.recoveryCodes = [];
      toastr.success('Two-factor authentication has been disabled');
      modal.close();
    });
  },

  loadPasskeys () {
    if (!this.passkeysSupported) return;
    this.passkeysLoading = true;
    this.$store.dispatch('user.listPasskeys').then((res) => {
      this.passkeys = res && res.data && Array.isArray(res.data.data) ? res.data.data : [];
      this.passkeysLoading = false;
    }).catch((error) => {
      this.passkeysLoading = false;
      toastr.error(responseMessage(error, 'Unable to load passkeys.'));
    });
  },

  openAddPasskey () {
    this.errors = {};
    this.passkeyName = '';
    this.$refs.addPasskeyModal.open();
  },

  registerPasskey (modal) {
    const name = this.passkeyName ? this.passkeyName.trim() : '';
    if (name.length > 100) {
      this.errors = { name: ['The passkey name may not be greater than 100 characters.'] };
      return;
    }

    this.errors = {};
    this.passkeyProcessing = true;
    this.$store.dispatch('user.registerPasskey', name || undefined).then((res) => {
      this.passkeyProcessing = false;
      const passkey = res && res.data && res.data.data;
      if (passkey) this.passkeys.unshift(passkey);
      this.passkeyName = '';
      modal.close();
      toastr.success('Passkey registered.');
    }).catch((error) => {
      this.passkeyProcessing = false;
      const errors = this.$store.state.user.errors;
      if (errors && errors.errors) this.errors = _.cloneDeep(errors.errors);
      toastr.error(webauthn.errorMessage(error, 'create'));
    });
  },

  openDeletePasskey (passkey) {
    this.passkeyToDelete = passkey;
    this.$refs.deletePasskeyModal.open();
  },

  deletePasskey (modal) {
    if (!this.passkeyToDelete) return;
    const id = this.passkeyToDelete.id;
    this.passkeyProcessing = true;
    this.$store.dispatch('user.deletePasskey', id).then(() => {
      this.passkeys = this.passkeys.filter((passkey) => passkey.id !== id);
      this.passkeyProcessing = false;
      this.passkeyToDelete = null;
      modal.close();
      toastr.success('Passkey deleted.');
    }).catch((error) => {
      this.passkeyProcessing = false;
      toastr.error(responseMessage(error, 'Unable to delete the passkey.'));
    });
  },

  formatPasskeyDate (value) {
    return value ? moment(value).format('MMM D, YYYY h:mm A') : 'Never';
  }
}

export default {
  name: 'profile-security',
  components,
  computed,
  methods,
  data () {
    return {
      errors: {},
      processing: false,
      setupActive: false,
      setupCode: '',
      disableCode: '',
      qr: null,
      secret: '',
      recoveryCodes: [],
      passkeysSupported: false,
      platformAuthenticatorAvailable: false,
      passkeysLoading: false,
      passkeyProcessing: false,
      passkeyName: '',
      passkeys: [],
      passkeyToDelete: null
    }
  },
  mounted () {
    this.passkeysSupported = webauthn.isSupported();
    if (!this.passkeysSupported) return;
    this.loadPasskeys();
    webauthn.isPlatformAuthenticatorAvailable().then((available) => {
      this.platformAuthenticatorAvailable = available;
    });
  }
}
